/**
 * Browser tab title helpers for the React admin SPA.
 *
 * Derives per-page titles from the permission catalog and the runtime
 * branding brand name (default "247 Alert").
 *
 * Title format: `{Page Label} - {brandName}`
 */

import { getPageByRoute } from '../../shared/security/permission-catalog';
import { isReactPublicRoute } from './auth-public-routes';

/** Default brand name when no branding settings are configured. */
const DEFAULT_BRAND_NAME = '247 Alert';

/**
 * Format a page label and brand name into a browser tab title.
 */
export function formatDocumentTitle(pageLabel: string, brandName?: string | null): string {
  const name = brandName?.trim() || DEFAULT_BRAND_NAME;
  return `${pageLabel} - ${name}`;
}

/**
 * Resolve the appropriate document title for a given pathname.
 *
 * - Public routes (e.g. `/login.html`) → "Sign in"
 * - Admin routes → catalog `label` or fallback "Admin"
 * - Unknown paths → brand name only
 */
export function resolveDocumentTitleForPath(
  pathname: string,
  brandName?: string | null,
): string {
  const trimmed = pathname.replace(/\/+$/, '') || '/';
  const name = brandName?.trim() || DEFAULT_BRAND_NAME;

  // Login / public routes
  if (isReactPublicRoute(trimmed)) {
    return formatDocumentTitle('Sign in', brandName);
  }

  // Root path "/" — same default as getReactAdminPage (reports admin)
  if (trimmed === '/') {
    const entry = getPageByRoute('/report-admin.html');
    if (entry?.label) {
      return formatDocumentTitle(entry.label, brandName);
    }
  }

  // Admin routes — resolve label from the permission catalog
  const catalogEntry = getPageByRoute(trimmed);
  if (catalogEntry?.label) {
    return formatDocumentTitle(catalogEntry.label, brandName);
  }

  // Unknown admin path — fallback label with brand name
  return formatDocumentTitle('Admin', brandName);
}
